// On-device store for bill photos staged against an unsubmitted petty cash /
// R&M draft. The typed rows of a draft live in localStorage (text only), which
// can't hold files — so without this, "Save draft" silently dropped every
// attached bill. IndexedDB can hold Blobs, so the files for each draft key are
// kept here as one record: an array (one entry per saved row) of file lists.
// Everything is best-effort: if IndexedDB is unavailable (private mode, old
// browser) the draft still saves its rows, just without the bills.

const DB_NAME = 'oh-pc-draft-bills'
const STORE = 'bills'

const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    const req = window.indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })

const run = async (mode, fn) => {
  const db = await openDb()
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const req = fn(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(req && req.result)
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  } finally {
    db.close()
  }
}

// Store as plain {name, type, blob} rather than File objects: some Safari
// versions can't put a File into IndexedDB, but every browser takes a Blob.
const pack = (f) => ({
  name: f.name || 'bill',
  type: f.type || '',
  lastModified: f.lastModified || Date.now(),
  blob: f instanceof Blob ? f.slice(0, f.size, f.type) : f
})

const unpack = (p) => {
  try {
    return new File([p.blob], p.name, {
      type: p.type,
      lastModified: p.lastModified
    })
  } catch (e) {
    // Very old browsers without the File constructor: a named Blob uploads fine.
    const b = p.blob
    b.name = p.name
    return b
  }
}

// filesPerRow: array aligned with the saved rows, each an array of File/Blob.
// No files at all → the record is removed.
export const saveDraftBills = async (key, filesPerRow) => {
  const hasAny = filesPerRow.some((fs) => fs && fs.length)
  return run('readwrite', (store) =>
    hasAny
      ? store.put(
          filesPerRow.map((fs) => (fs || []).map(pack)),
          key
        )
      : store.delete(key)
  )
}

// → array aligned with the saved rows (each an array of File), or [] if none.
export const loadDraftBills = async (key) => {
  const rec = await run('readonly', (store) => store.get(key))
  if (!Array.isArray(rec)) return []
  return rec.map((fs) => (Array.isArray(fs) ? fs.map(unpack) : []))
}

export const clearDraftBills = (key) =>
  run('readwrite', (store) => store.delete(key)).catch(() => {})
